import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// ── Query key factories ──

// Filter + sort state that parameterizes a paginated list query key. Mirrors
// `DataGridQuery` from `@/hooks/use-data-grid`; kept structural to avoid a cycle.
interface ListKeyFilters {
  search?: string;
  status?: string;
  sort?: string;
  sortDir?: string;
}

// Reference entity (src/examples/widget). One factory per domain; mirror this
// shape for a real entity and invalidate via `*.all` after mutations.
export const widgetKeys = {
  all: ["widgets"] as const,
  list: (filters: ListKeyFilters) => ["widgets", "list", filters] as const,
  detail: (id: string) => ["widgets", "detail", id] as const,
};

export const fileKeys = {
  all: ["files"] as const,
  list: (filters: ListKeyFilters) => ["files", "list", filters] as const,
  detail: (id: string) => ["files", "detail", id] as const,
} as const;

export const userKeys = {
  all: ["users"] as const,
  list: (filters?: { search?: string }) => ["users", "list", filters] as const,
} as const;

export const devKeys = {
  seedAccounts: ["dev", "seed-accounts"] as const,
} as const;

export const emailKeys = {
  all: ["email"] as const,
  outbox: () => ["email", "outbox"] as const,
  templates: () => ["email", "templates"] as const,
  template: (key: string) => ["email", "templates", key] as const,
} as const;

export const auditKeys = {
  all: ["audit"] as const,
  list: (filters: ListKeyFilters) => ["audit", "list", filters] as const,
} as const;

export const buInstanceKeys = {
  all: ["bu-instances"] as const,
  list: (filters: ListKeyFilters) => ["bu-instances", "list", filters] as const,
  detail: (id: string) => ["bu-instances", "detail", id] as const,
  active: ["bu-instances", "active"] as const,
} as const;

export const masterDataMappingKeys = {
  all: ["master-data-mappings"] as const,
  list: (filters: ListKeyFilters & { sourceBuId?: string; targetBuId?: string }) =>
    ["master-data-mappings", "list", filters] as const,
  detail: (id: string) => ["master-data-mappings", "detail", id] as const,
} as const;

export const soapCallLogKeys = {
  all: ["soap-call-log"] as const,
  list: (filters: ListKeyFilters & { operation?: string; buInstanceId?: string }) =>
    ["soap-call-log", "list", filters] as const,
  detail: (id: string) => ["soap-call-log", "detail", id] as const,
} as const;

export const soapApiLogSettingsKeys = {
  all: ["soap-api-log-settings"] as const,
} as const;

export const buInstanceLabelKeys = {
  all: ["bu-instance-label"] as const,
} as const;

export const masterDataExtractedValueKeys = {
  all: ["master-data-extracted-values"] as const,
  list: (filters: { buInstanceId: string; entityType: string; search?: string }) =>
    ["master-data-extracted-values", "list", filters] as const,
} as const;

/**
 * Deliberately separate from `masterDataExtractedValueKeys` above — that one
 * backs the admin Master Data Mappings picker (`use-master-data-extracted-values.ts`,
 * caches the raw `{ data: [...] }` response envelope), while this backs the
 * copy-tool staging editor's reference dropdowns (`use-extracted-values.ts`,
 * caches an already-unwrapped `string[]`). Sharing one key namespace let
 * TanStack Query serve one hook's cached shape to the other, crashing
 * `AsyncCombobox` with "options.some is not a function" whenever both had
 * queried the same (BU, entity type) with no search term.
 */
export const copyOperationExtractedValueKeys = {
  all: ["copy-operation-extracted-values"] as const,
  list: (filters: { buInstanceId: string; entityType: string; search?: string }) =>
    ["copy-operation-extracted-values", "list", filters] as const,
} as const;

export const copyOperationKeys = {
  all: ["copy-operations"] as const,
  list: (filters: ListKeyFilters & { objectType?: string }) =>
    ["copy-operations", "list", filters] as const,
  detail: (id: string) => ["copy-operations", "detail", id] as const,
  priorAttempts: (
    params: {
      sourceBuId: string;
      sourceObjectId: string;
      targetBuId: string;
      targetCustomerId: string;
    } | null,
  ) => ["copy-operations", "prior-attempts", params] as const,
} as const;

export const contractSearchKeys = {
  last: ["contract-search", "last"] as const,
} as const;

export const privateHireSearchKeys = {
  last: ["private-hire-search", "last"] as const,
} as const;

export const clientSearchKeys = {
  last: ["client-search", "last"] as const,
} as const;
